import {
  DEFAULT_STATS_RANGE,
  STATS_RANGES,
  type AppStats,
  type StatsRange,
} from '@talkofthetown/shared';
import { useEffect, useState } from 'react';
import { callables } from '../firebase';
import { friendlyError } from '../lib/errors';
import { countryName } from '../lib/places';
import { Dialog } from './Dialog';

interface Props {
  open: boolean;
  onClose: () => void;
}

const number = new Intl.NumberFormat();
const rangeLabel = (r: StatsRange) => (r === null ? 'All time' : `Last ${r} days`);

/** Admins: how many real people use the app, over a range of days, and from where. */
export function StatsDialog({ open, onClose }: Props) {
  const [range, setRange] = useState<StatsRange>(DEFAULT_STATS_RANGE);
  // The last answer, with the range it's for (a new range shows "Counting…" until its answer).
  const [result, setResult] = useState<{ range: StatsRange; stats?: AppStats; error?: string }>();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    callables
      .adminStats({ range })
      .then((res) => !cancelled && setResult({ range, stats: res.data }))
      .catch((err) => !cancelled && setResult({ range, error: friendlyError(err) }));
    return () => {
      cancelled = true;
    };
  }, [open, range]);

  const current = result?.range === range ? result : undefined;
  const stats = current?.stats;
  const people = stats?.people;
  const figures: Array<[string, number | undefined, string?]> = [
    [
      'People who visited',
      people?.visited,
      people && `${people.capped ? 'at least; ' : ''}${number.format(people.newcomers)} new`,
    ],
    ['Regulars', people?.regulars, 'came back another day'],
    ['Signed in with Google', people?.signedIn],
    [
      'Posts by people',
      stats?.posts.people,
      stats && `and ${number.format(stats.posts.ai)} by the AI news desk`,
    ],
  ];

  return (
    <Dialog open={open} onClose={onClose} labelledBy="stats-title" className="stats">
      <h2 id="stats-title">Who’s using Talk of the Town</h2>
      <div className="chips stats__ranges" role="group" aria-label="Time range">
        {STATS_RANGES.map((r) => (
          <button
            key={String(r)}
            type="button"
            className="chip"
            aria-pressed={r === range}
            onClick={() => setRange(r)}
          >
            {rangeLabel(r)}
          </button>
        ))}
      </div>
      {current?.error ? (
        <p role="alert">{current.error}</p>
      ) : !stats ? (
        <p className="muted">Counting…</p>
      ) : (
        <>
          <dl className="stats__figures">
            {figures.map(([label, value, note]) => (
              <div key={label} className="stats__figure">
                <dt>{label}</dt>
                <dd>
                  {number.format(value ?? 0)}
                  {note && <span className="muted">{note}</span>}
                </dd>
              </div>
            ))}
          </dl>
          {stats.countries.length > 0 && (
            <div className="stats__scroll">
              <table className="stats__table">
                <caption>By country</caption>
                <thead>
                  <tr>
                    <th scope="col">Country</th>
                    <th scope="col">People</th>
                    <th scope="col">Regulars</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.countries.map((c) => (
                    <tr key={c.country ?? 'unknown'}>
                      <th scope="row">{c.country ? countryName(c.country) : 'Not known'}</th>
                      <td>{number.format(c.people)}</td>
                      <td>{number.format(c.regulars)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="muted stats__note">
            Each browser counts as one person; yours, automated browsers and the AI news desk aren’t
            counted. Regulars are the surest sign of real people. Countries come from each browser’s
            time zone, so they’re approximate; “Not known” are mostly visits from before we noted
            it.
          </p>
        </>
      )}
    </Dialog>
  );
}
